import { trackFinishButtonClick, trackSelectExerciseButtonClick } from 'analytics/trackers/exercise';
import { trackWorkoutCompleteSuccess } from 'analytics/trackers/workout';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';

import { closeModal, openModal } from 'store/modals/actions';
import { ModalName } from 'store/modals/types';
import { selectCurrentUser } from 'store/user/selectors';
import { workoutComplete } from 'store/workouts/actions';

import { useDispatch, useSelector } from 'hooks';
import { useWorkout } from 'hooks/workout/useWorkout';

import isMobile from 'helpers/isMobile';

import { ExerciseType } from 'types/exercise';
import { WorkoutCompleteParamsType, WorkoutPlanCategory } from 'types/workouts';

import { DEFAULT_SPEND_TIME } from './constants';
import WorkoutContext from './context';
import { calculateBurnedCalories, fancyTime, getWorkoutPlanByHash, getWorkoutPlanByWorkoutId } from './helpers';
import { ProgressDataType } from './types';

const WorkoutProvider = ({ children }: { children: ReactNode }) => {
    const { exercises: exerciseMap } = useSelector((state) => state.exercises);
    const { weight } = useSelector(selectCurrentUser);

    const dispatch = useDispatch();
    const [currentIndex, setCurrentIndex] = useState(0);
    const playerContainer = useRef<null | HTMLDivElement>(null);

    const [progressStore, setProgress] = useState<{ [key: string]: ProgressDataType }>({});

    const [completeData, setCompleteData] = useState<WorkoutCompleteParamsType | null>(null);

    const blocker = useBlocker(Boolean(!completeData));

    const [totalProgress, updateTotalProgress] = useState(0);

    const { workout, playlist, plan, workoutExercises } = useWorkout();

    const prevExercise = playlist[currentIndex - 1];
    const currentExercise = playlist[currentIndex];
    const nextExercise = playlist[currentIndex + 1];

    const exerciseData = exerciseMap[currentExercise.id];
    const nextExerciseData = nextExercise && exerciseMap[nextExercise.id];

    const currentPlaylistPosition = currentIndex + 1;

    const isPageBlocked = blocker.state === 'blocked';

    const stayOnWorkoutPage = () => {
        dispatch(closeModal());
        blocker.reset?.();
    };

    const closeWorkoutPage = () => {
        dispatch(closeModal());
        blocker.proceed?.();
    };

    const toNextExercise = () => {
        const nextIndex = currentIndex + 1;

        if (nextIndex === playlist.length) {
            return onCompleteWorkout();
        }

        setCurrentIndex(nextIndex);
    };

    const toPrevExercise = () => {
        if (!currentIndex) return;

        setCurrentIndex(currentIndex - 1);
    };

    const handleProgress = (id: number, progressData: ProgressDataType) => {
        setProgress({ ...progressStore, [id]: progressData });
        updateTotalProgress(totalProgress + 1);
    };

    const handleExerciseClick = (selectedExercise: ExerciseType) => {
        trackSelectExerciseButtonClick(exerciseData.id, currentPlaylistPosition);
        const selectedExerciseIndex = playlist.findIndex(({ id }) => id === selectedExercise.id);

        setCurrentIndex(selectedExerciseIndex);

        if (playerContainer.current && isMobile.any()) {
            playerContainer.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const fancyProgressTime = fancyTime(totalProgress);
    const fullWorkoutTime = fancyTime(totalProgress, true);

    const onCompleteWorkout = () => {
        if (!workout) return;

        trackFinishButtonClick(exerciseData.id, currentPlaylistPosition);

        const { id, created_at, planHash } = workout;

        const { challenge_id, challenge_position, category } = planHash
            ? getWorkoutPlanByHash(plan, planHash) || {}
            : getWorkoutPlanByWorkoutId(plan, id) || {};
        const isMainWorkout = category === WorkoutPlanCategory.Main;

        const exercisesData = workoutExercises.map(({ id, repetitions, time }) => {
            const commonData = { id, time };

            return repetitions === null ? commonData : { ...commonData, repetitions };
        });

        const params = {
            workout_id: id,
            calories_burned: calculateBurnedCalories({ playlist, exercises: exerciseMap, weight: weight || 80 }),
            time_spent: totalProgress || DEFAULT_SPEND_TIME,
            created_at,
            challenge_id: challenge_id ?? null,
            challenge_position: challenge_position ?? null,
            is_main_workout: isMainWorkout,
            exercise_data: exercisesData,
        };

        setCompleteData(params);

        dispatch(
            workoutComplete({
                params,
                onSuccess: trackWorkoutCompleteSuccess,
                onError: console.warn,
            })
        );
    };

    const onDisplayReasonAbandon = () => {
        dispatch(openModal(ModalName.workoutReasonAbandon, { onClose: closeWorkoutPage }));
    };

    useEffect(() => {
        if (isPageBlocked) {
            dispatch(
                openModal(ModalName.confirmCancelWorkout, {
                    onCancel: stayOnWorkoutPage,
                    onSubmit: onDisplayReasonAbandon,
                })
            );
        }
    }, [isPageBlocked, stayOnWorkoutPage, onDisplayReasonAbandon]);

    const handleNavigateExercise =
        (moveFunction: () => void, trackFunction: (id: number, position: number) => void) => () => {
            moveFunction();
            trackFunction(exerciseData.id, currentPlaylistPosition);
        };

    const value = {
        handleNavigateExercise,
        fancyProgressTime,
        fullWorkoutTime,
        handleExerciseClick,
        prevExercise,
        exerciseData,
        nextExerciseData,
        completeData,
        isComplete: Boolean(completeData),
        toNextExercise,
        toPrevExercise,
        handleProgress,
        currentExercise,
        currentIndex,
        currentPlaylistPosition,
        progressStore,
        playerContainer,
        isPageBlocked,
    };

    return <WorkoutContext.Provider value={value}> {children} </WorkoutContext.Provider>;
};

export default WorkoutProvider;
